import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { lerCamara, mudouOSuficiente, porCamara } from '@viewer/room/roomCamera'
import type { EstadoCamara } from '@viewer/room/roomProtocol'
import type { RefObject } from 'react'
import type { LigacaoDoCanal } from '@modules/bcf/lib/bcfCanalAoVivo'
import {
  LEITURA_DA_CAMARA_MS,
  VISTA_COMPLETA_MS,
  alvosDaSeleccao,
  chaveDaSeleccao,
  mensagemDaVista,
  seleccaoParaEnviar,
  type VistaRecebida,
} from './acompanhar'
import { primeiroNome, type PessoaOnline } from './presencaNaSessao'

export interface QuemAcompanho {
  utilizador: string
  nome: string
}

interface ControlosComEventos {
  addEventListener: (tipo: string, f: () => void) => void
  removeEventListener: (tipo: string, f: () => void) => void
}

function controlosDaCamara(): ControlosComEventos | null {
  const world = viewerApi.getWorld() as unknown as { camera?: { controls?: ControlosComEventos } } | null
  return world?.camera?.controls ?? null
}

/** Acompanhar alguém na sessão partilhada (ver acompanhar.ts).
 *
 *  Esta janela manda a sua vista enquanto houver mais alguém na sessão e
 *  ela própria não estiver a acompanhar ninguém (quem acompanha não
 *  conduz: evita que duas janelas se puxem uma à outra). Quem acompanha
 *  aplica a câmara e a selecção de quem escolheu, e deixa de acompanhar
 *  ao mexer na câmara ou quando essa pessoa sai. */
export function useAcompanharNaSessao(
  ligacao: RefObject<LigacaoDoCanal | null>,
  ligado: boolean,
  outras: readonly PessoaOnline[],
) {
  const [acompanho, setAcompanho] = useState<QuemAcompanho | null>(null)
  // O que as funções estáveis leem; actualiza-se onde o estado muda.
  const acompanhoRef = useRef<QuemAcompanho | null>(null)
  const seleccaoAplicada = useRef<string | null>(null)

  const parar = useCallback((motivo?: string) => {
    const antes = acompanhoRef.current
    acompanhoRef.current = null
    setAcompanho(null)
    seleccaoAplicada.current = null
    if (antes && motivo) {
      useAppStore.getState().pushToast(`Deixou de acompanhar ${primeiroNome(antes.nome)}: ${motivo}.`, {
        type: 'info',
      })
    }
  }, [])

  const comecar = useCallback((p: PessoaOnline) => {
    seleccaoAplicada.current = null
    const novo = { utilizador: p.utilizador, nome: p.nome }
    acompanhoRef.current = novo
    setAcompanho(novo)
  }, [])

  /** Chamar com cada vista recebida pelo canal. */
  const aoReceberVista = useCallback((v: VistaRecebida) => {
    const alvo = acompanhoRef.current
    if (!alvo || alvo.utilizador !== v.utilizador) return
    if (v.camara) void porCamara(v.camara)
    if (v.seleccao) {
      const chave = chaveDaSeleccao(v.seleccao)
      if (chave === seleccaoAplicada.current) return
      seleccaoAplicada.current = chave
      const s = useAppStore.getState()
      const alvos = alvosDaSeleccao(v.seleccao, s.models)
      if (alvos.length === 0) s.clearSelection()
      else s.applyBulkSelection(alvos, 'replace')
    }
  }, [])

  // A pessoa que acompanho saiu da sessão.
  const presentes = outras.map((p) => p.utilizador).join(',')
  useEffect(() => {
    const alvo = acompanhoRef.current
    if (alvo && !presentes.split(',').includes(alvo.utilizador)) parar('saiu da sessão')
    if (alvo && !ligado) parar('perdeu-se a ligação')
  }, [presentes, ligado, parar])

  // Mexer na câmara à mão é deixar de acompanhar. O `controlstart` só
  // nasce de gestos da pessoa; as transições do porCamara não o disparam.
  useEffect(() => {
    if (!acompanho) return
    const c = controlosDaCamara()
    if (!c) return
    const aoMexer = () => parar('mexeu na câmara')
    c.addEventListener('controlstart', aoMexer)
    return () => c.removeEventListener('controlstart', aoMexer)
  }, [acompanho, parar])

  // Mandar a minha vista: com mais alguém na sessão e sem estar a
  // acompanhar ninguém.
  const haOutros = outras.length > 0
  useEffect(() => {
    const canal = ligacao.current
    if (!canal || !ligado || !haOutros || acompanho) return
    let ultimaCamara: EstadoCamara | null = null
    let ultimaSeleccao: string | null = null
    const seleccaoActual = () => {
      const s = useAppStore.getState()
      return seleccaoParaEnviar(s.selectedTargets, s.models)
    }
    const mandar = (camara: EstadoCamara | null, seleccao: ReturnType<typeof seleccaoActual> | null) => {
      const m = mensagemDaVista(camara, seleccao)
      if (m) canal.enviar(m)
    }
    const lerEMandarCamara = () => {
      const c = lerCamara()
      if (!c || !mudouOSuficiente(ultimaCamara, c)) return
      ultimaCamara = c
      mandar(c, null)
    }
    const mandarTudo = () => {
      const c = lerCamara()
      if (c) ultimaCamara = c
      const sel = seleccaoActual()
      ultimaSeleccao = chaveDaSeleccao(sel)
      mandar(c, sel)
    }
    mandarTudo()
    const leitura = window.setInterval(lerEMandarCamara, LEITURA_DA_CAMARA_MS)
    const completa = window.setInterval(mandarTudo, VISTA_COMPLETA_MS)
    const largar = useAppStore.subscribe((s, antes) => {
      if (s.selectedTargets === antes.selectedTargets) return
      const sel = seleccaoParaEnviar(s.selectedTargets, s.models)
      const chave = chaveDaSeleccao(sel)
      if (chave === ultimaSeleccao) return
      ultimaSeleccao = chave
      mandar(null, sel)
    })
    return () => {
      window.clearInterval(leitura)
      window.clearInterval(completa)
      largar()
    }
  }, [ligacao, ligado, haOutros, acompanho])

  return { acompanho, comecar, parar, aoReceberVista }
}
